import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 車両証明書削除指示API
 */
export const useDeleteCertificateAPI = (): {
  state: APIState;
  deleteCertificate: (vehicleId: string) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const deleteCertificateAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.delete(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/certificates`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const deleteCertificate = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.delete_vehicle_certificate', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await deleteCertificateAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.delete_vehicle_certificate', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.delete_vehicle_certificate', {
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId:
              'vehicle_certificate_delete_snackbar_success-status-div',
            closeButtonTestId:
              'vehicle_certificate_delete_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, deleteCertificateAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    deleteCertificate,
  };
};
